/**
 * Settings → Email Templates.
 *
 * Lists every known template key, edits subject + body. A "default"
 * badge flags keys the org hasn't customized yet — editing one lazily
 * creates the override.
 */
import { useEffect, useMemo, useState } from "react";
import { Mail, RotateCcw, Save } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  listEmailTemplates,
  previewEmailTemplate,
  resetEmailTemplate,
  updateEmailTemplate,
  type EmailTemplate,
} from "@/api/emailTemplates";

function humanize(key: string): string {
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function EmailTemplatesSettings() {
  const { addToast } = useToast();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState<{
    subject: string;
    body: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listEmailTemplates();
      // Always set state, even for empty arrays — the sidebar renders a
      // friendly "no templates" empty state rather than crashing. This
      // also clears a prior error banner if a retry succeeds.
      setTemplates(data);
      setLoadError(null);
      if (!selectedKey && data.length > 0) {
        selectTemplate(data[0]);
      }
    } catch (err: unknown) {
      // Surface the real backend detail instead of a generic "Failed to
      // load". A 403 (wrong role) and a 500 (serialization bug) look
      // nothing alike to an operator, and the generic toast was making
      // the root cause invisible in prod.
      const ax = err as {
        response?: { status?: number; data?: { detail?: unknown } };
        message?: string;
      };
      const status = ax?.response?.status;
      const detail = ax?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : status === 403
            ? "You don't have permission to view email templates."
            : status === 401
              ? "Your session expired — please sign in again."
              : `Failed to load email templates${status ? ` (HTTP ${status})` : ""}.`;
      console.error("EmailTemplatesSettings: list failed", { status, detail, err });
      setLoadError(msg);
      addToast("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (t: EmailTemplate) => {
    setSelectedKey(t.template_key);
    setSubject(t.subject);
    setBody(t.body);
    setPreview(null);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(
    () => templates.find((t) => t.template_key === selectedKey) ?? null,
    [templates, selectedKey],
  );

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateEmailTemplate(selected.template_key, { subject, body });
      addToast("success", "Template saved");
      await load();
    } catch {
      addToast("error", "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!selected) return;
    try {
      await resetEmailTemplate(selected.template_key);
      addToast("success", "Reset to default");
      await load();
    } catch {
      addToast("error", "Failed to reset");
    }
  };

  const showPreview = async () => {
    if (!selected) return;
    try {
      // Build sample variables from the schema's advertised list.
      const vars: Record<string, string> = {};
      for (const v of selected.available_variables) {
        vars[v] = `[${v}]`;
      }
      const p = await previewEmailTemplate(selected.template_key, {
        subject,
        body,
        variables: vars,
      });
      setPreview(p);
    } catch {
      addToast("error", "Preview failed");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Templates</CardTitle>
      </CardHeader>
      <div className="grid grid-cols-12 gap-4">
        {/* Sidebar — list of template keys */}
        <div className="col-span-12 md:col-span-4 border-r border-border/30 pr-3">
          <div className="space-y-1">
            {templates.map((t) => (
              <button
                key={t.template_key}
                onClick={() => selectTemplate(t)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedKey === t.template_key
                    ? "bg-accent/10 text-text-primary"
                    : "hover:bg-surface-hover text-text-muted"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">
                    {humanize(t.template_key)}
                  </span>
                  {t.is_default && (
                    <Badge variant="default">Default</Badge>
                  )}
                </div>
              </button>
            ))}
            {templates.length === 0 && !loading && (
              <div className="py-8 text-center text-sm text-text-muted">
                <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" />
                {loadError ? (
                  <>
                    <p className="mb-3">{loadError}</p>
                    <Button variant="secondary" size="sm" onClick={load}>
                      Retry
                    </Button>
                  </>
                ) : (
                  "No templates available."
                )}
              </div>
            )}
            {loading && templates.length === 0 && (
              <div className="py-8 text-center text-sm text-text-muted">
                Loading…
              </div>
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="col-span-12 md:col-span-8 space-y-3">
          {selected ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">
                    {humanize(selected.template_key)}
                  </h3>
                  <p className="text-xs text-text-muted">
                    Variables:{" "}
                    {selected.available_variables.length > 0 ? (
                      selected.available_variables.map((v) => (
                        <code
                          key={v}
                          className="mx-1 px-1 py-0.5 rounded bg-surface text-[11px]"
                        >{`{{${v}}}`}</code>
                      ))
                    ) : (
                      <span>(none)</span>
                    )}
                  </p>
                </div>
                {!selected.is_default && (
                  <Button variant="ghost" size="sm" onClick={reset}>
                    <RotateCcw className="h-4 w-4" />
                    Reset
                  </Button>
                )}
              </div>

              <Input
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />

              <div>
                <label className="block text-xs font-medium text-text-muted mb-1">
                  Body
                </label>
                <textarea
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
                  rows={10}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={showPreview}>
                  Preview
                </Button>
                <Button onClick={save} disabled={saving}>
                  <Save className="h-4 w-4" />
                  Save
                </Button>
              </div>

              {preview && (
                <div className="rounded-lg border border-border/50 bg-surface p-3 text-sm">
                  <div className="text-xs uppercase text-text-muted">
                    Subject
                  </div>
                  <div className="font-medium">{preview.subject}</div>
                  <div className="text-xs uppercase text-text-muted mt-3">
                    Body
                  </div>
                  <pre className="whitespace-pre-wrap text-text-primary">
                    {preview.body}
                  </pre>
                </div>
              )}
            </>
          ) : (
            <div className="py-8 text-center text-sm text-text-muted">
              Select a template to edit.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
